import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDT, useApi } from "@/lib/api";
import { passkeyAvailable, registerPasskey, hasLocalPasskey, forgetLocalPasskey } from "@/lib/passkey";

function useAvailable() {
  const [ok, setOk] = useState(false);
  useEffect(() => { passkeyAvailable().then(setOk); }, []);
  return ok;
}

export function PasskeySection() {
  const ok = useAvailable();
  const list = useApi("/passkeys");
  const [busy, setBusy] = useState(false);
  const add = async () => { setBusy(true); try { await registerPasskey(); toast.success("Fingerprint / face sign-in is on for this device"); list.reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const remove = async (p) => {
    if (!window.confirm(`Remove fingerprint / face sign-in for "${p.device}"?`)) return;
    try { await api.delete(`/passkeys/${p.id}`); if (p.this_site) forgetLocalPasskey(); toast.success("Removed"); list.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="surface p-6" data-testid="passkey-section">
      <h3 className="font-semibold flex items-center gap-2"><Fingerprint className="h-4 w-4 text-[#2412B5]" />Fingerprint / face sign-in</h3>
      <p className="text-sm muted mt-1">Sign in with one touch instead of an SMS code. Your fingerprint or face never leaves your phone.</p>
      <div className="mt-4 divide-y divide-[#E2E8ED]">{(list.data || []).map((p) => (
        <div key={p.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`passkey-row-${p.id}`}>
          <span>{p.device}<span className="block text-xs muted">Added {fmtDT(p.created_at)}{p.last_used_at ? ` · last used ${fmtDT(p.last_used_at)}` : ""}</span></span>
          <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`passkey-remove-${p.id}`} aria-label="Remove"><Trash2 className="h-4 w-4 text-red-600" /></Button>
        </div>))}</div>
      {ok && hasLocalPasskey() && (list.data || []).some((p) => p.this_site) ? <p className="text-sm text-[#086B2D] mt-3" data-testid="passkey-on-this-device">On for this device</p>
        : ok ? <Button className="mt-3" variant="outline" onClick={add} disabled={busy} data-testid="passkey-add-btn"><Fingerprint className="h-4 w-4 mr-1" />Turn on for this device</Button>
        : <p className="text-xs muted mt-3" data-testid="passkey-unsupported">This device or browser doesn't support fingerprint / face sign-in.</p>}
    </section>
  );
}

export function PasskeyPrompt() {
  const ok = useAvailable();
  const [hide, setHide] = useState(() => hasLocalPasskey() || localStorage.getItem("nt_passkey_dismissed") === "1");
  if (!ok || hide) return null;
  const dismiss = () => { localStorage.setItem("nt_passkey_dismissed", "1"); setHide(true); };
  const add = async () => { try { await registerPasskey(); toast.success("Done – next time just use your fingerprint or face"); setHide(true); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="surface p-4 mb-6 flex items-center gap-3 border-l-4 border-[#2412B5]" data-testid="passkey-prompt">
      <Fingerprint className="h-6 w-6 text-[#2412B5] shrink-0" />
      <p className="text-sm flex-1"><b>Skip the SMS code next time.</b> <span className="muted">Turn on fingerprint / face sign-in for this phone.</span></p>
      <Button size="sm" className="rounded-full" onClick={add} data-testid="passkey-prompt-enable-btn">Turn on</Button>
      <button onClick={dismiss} aria-label="Not now" className="muted" data-testid="passkey-prompt-dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
}
